/**
 * useSnapshot Hook
 * 
 * React hook for managing user snapshots in components.
 * Provides easy-to-use functions for creating, sharing, and managing snapshots.
 */

import { useState, useCallback, useEffect } from 'react';
import { useApp } from '@/context/AppContext';
import { 
  createUserSnapshot, 
  retrieveUserSnapshot, 
  updateSnapshotStage,
  shareSnapshot,
  generateShareMessage,
  markSnapshotUsed,
  restoreFromSnapshot,
  isSnapshotMode,
  SnapshotData,
  CreateSnapshotParams
} from '@/services/snapshotService';
import { BundleInfo } from '@/services/snapshotService';

interface UseSnapshotReturn {
  // State
  currentSnapshot: SnapshotData | null;
  isLoading: boolean;
  error: string | null;
  
  // Actions  
  createSnapshot: (params: CreateSnapshotParams) => Promise<SnapshotData | null>;
  loadSnapshot: (snapshotId: string) => Promise<SnapshotData | null>;
  updateStage: (stage: 'productos' | 'plan-seleccion' | 'carrito', bundleInfo?: BundleInfo) => Promise<void>;
  shareSnapshotUrl: () => Promise<void>;
  getShareMessage: () => string;
  markAsUsed: () => Promise<void>;
  restoreState: () => void;
  
  // Utilities
  isInSnapshotMode: boolean;
  snapshotId: string | null;
}

export const useSnapshot = (): UseSnapshotReturn => {
  const { state, updateEleccionProgress, updateDispositivo } = useApp();
  const [currentSnapshot, setCurrentSnapshot] = useState<SnapshotData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Check if we're in snapshot mode
  const snapshotId = isSnapshotMode();
  const isInSnapshotMode = !!snapshotId;

  /**
   * Create a new snapshot
   */
  const createSnapshot = useCallback(async (params: CreateSnapshotParams): Promise<SnapshotData | null> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const snapshot = await createUserSnapshot(state, params);
      setCurrentSnapshot(snapshot);
      return snapshot;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create snapshot';
      setError(errorMessage);
      console.error('useSnapshot - createSnapshot error:', err);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [state]);

  /**
   * Load an existing snapshot
   */
  const loadSnapshot = useCallback(async (id: string): Promise<SnapshotData | null> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const snapshot = await retrieveUserSnapshot(id);
      setCurrentSnapshot(snapshot);
      return snapshot;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load snapshot';
      setError(errorMessage);
      console.error('useSnapshot - loadSnapshot error:', err);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Update current snapshot stage
   */
  const updateStage = useCallback(async (
    stage: 'productos' | 'plan-seleccion' | 'carrito',
    bundleInfo?: BundleInfo
  ): Promise<void> => {
    if (!currentSnapshot) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      await updateSnapshotStage(currentSnapshot.id, stage, state, bundleInfo);
      
      // Update local snapshot data
      setCurrentSnapshot(prev => prev ? {
        ...prev,
        metadata: {
          ...prev.metadata,
          stage
        }
      } : null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update snapshot';
      setError(errorMessage);
      console.error('useSnapshot - updateStage error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [currentSnapshot, state]);

  /**
   * Share snapshot URL
   */
  const shareSnapshotUrl = useCallback(async (): Promise<void> => {
    if (!currentSnapshot) {
      setError('No snapshot available to share');
      return;
    }
    
    try {
      await shareSnapshot(currentSnapshot);
      console.log('Snapshot URL copied to clipboard');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to share snapshot';
      setError(errorMessage);
      console.error('useSnapshot - shareSnapshotUrl error:', err);
    }
  }, [currentSnapshot]);

  /**
   * Get shareable message
   */
  const getShareMessage = useCallback((): string => {
    if (!currentSnapshot) {
      return 'No snapshot available';
    }
    return generateShareMessage(currentSnapshot);
  }, [currentSnapshot]);

  /**
   * Mark snapshot as used
   */
  const markAsUsed = useCallback(async (): Promise<void> => {
    if (!currentSnapshot) return;
    
    try {
      await markSnapshotUsed(currentSnapshot.id);
      
      // Update local snapshot data
      setCurrentSnapshot(prev => prev ? {
        ...prev,
        status: 'used'
      } : null);
    } catch (err) {
      console.error('useSnapshot - markAsUsed error:', err);
      // Don't set error state as this is not critical
    }
  }, [currentSnapshot]);

  /**
   * Restore app state from current snapshot
   */
  const restoreState = useCallback((): void => {
    if (!currentSnapshot) {
      setError('No snapshot available to restore');
      return;
    }
    
    try {
      restoreFromSnapshot(currentSnapshot, updateEleccionProgress, updateDispositivo);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to restore state';
      setError(errorMessage);
      console.error('useSnapshot - restoreState error:', err);
    }
  }, [currentSnapshot, updateEleccionProgress, updateDispositivo]);

  /**
   * Auto-load snapshot if in snapshot mode
   */
  useEffect(() => {
    if (snapshotId && !currentSnapshot) {
      loadSnapshot(snapshotId);
    }
  }, [snapshotId, currentSnapshot, loadSnapshot]);

  return {
    // State
    currentSnapshot,
    isLoading,
    error,
    
    // Actions
    createSnapshot,
    loadSnapshot,
    updateStage,
    shareSnapshotUrl,
    getShareMessage,
    markAsUsed,
    restoreState,
    
    // Utilities
    isInSnapshotMode,
    snapshotId
  };
};